/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

// import { addClassNamesToElement, removeClassNamesFromElement } from "@lexical/utils";
import { addClassNamesToElement } from "@lexical/utils";
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    RangeSelection,
    SerializedElementNode,
    Spread,
} from "lexical";
import { $createParagraphNode, $createTextNode, $isElementNode, ElementNode, TextNode } from "lexical";

import { $isArticleContainerNode, ArticleContainerNode } from "./ArticleContainerNode";
import { $isArticleContentNode } from "./ArticleContentNode";

type SerializedArticleTitleNode = Spread<
    {
        show: boolean;
    },
    SerializedElementNode
>;

export function convertSummaryElement(domNode: HTMLElement): DOMConversionOutput | null {
    const node = $createArticleTitleNode(true);
    return {
        node,
    };
}

export class ArticleTitleNode extends ElementNode {
    __show: boolean = true;

    constructor(show: boolean, key?: NodeKey) {
        super(key);
        this.__show = show;
    }

    static getType(): string {
        return "article-title";
    }

    getShow(): boolean {
        const self = this.getLatest();
        return self.__show;
    }

    setShow(value: boolean) {
        const self = this.getWritable();
        self.__show = value;
    }

    toggleShow(): void {
        this.setShow(!this.getShow());
    }

    static clone(node: ArticleTitleNode): ArticleTitleNode {
        return new ArticleTitleNode(node.__show, node.__key);
    }

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
        const dom = document.createElement("summary");
        addClassNamesToElement(dom, config.theme.articleTitle);
        dom.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
        return dom;
    }

    updateDOM(prevNode: ArticleTitleNode, dom: HTMLElement): boolean {
        return false;
    }

    static importDOM(): DOMConversionMap | null {
        return {
            summary: (domNode: HTMLElement) => {
                return {
                    conversion: convertSummaryElement,
                    priority: 1,
                };
            },
        };
    }

    static importJSON(serializedNode: SerializedArticleTitleNode): ArticleTitleNode {
        return $createArticleTitleNode(serializedNode.show);
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("summary");
        addClassNamesToElement(element, "Article__Title");
        return { element };
    }

    exportJSON(): SerializedArticleTitleNode {
        return {
            ...super.exportJSON(),
            show: this.__show,
            type: "article-title",
            version: 1,
        };
    }

    collapseAtStart(_selection: RangeSelection): boolean {
        this.getParentOrThrow().insertBefore(this);
        return true;
    }

    insertNewAfter(_: RangeSelection, restoreSelection = true): ElementNode {
        const containerNode = this.getParentOrThrow();

        if (!$isArticleContainerNode(containerNode)) {
            throw new Error("ArticleTitleNode expects to be child of ArticleContainerNode");
        }

        if (containerNode.getOpen()) {
            const contentNode = this.getNextSibling();
            if (!$isArticleContentNode(contentNode)) {
                throw new Error("ArticleTitleNode expects to have ArticleContentNode sibling");
            }

            const firstChild = contentNode.getFirstChild();
            if ($isElementNode(firstChild)) {
                return firstChild;
            } else {
                const paragraph = $createParagraphNode();
                contentNode.append(paragraph);
                return paragraph;
            }
        } else {
            const paragraph = $createParagraphNode();
            containerNode.insertAfter(paragraph, restoreSelection);
            return paragraph;
        }
    }
}

export function $createArticleTitleNode(show: boolean): ArticleTitleNode {
    return new ArticleTitleNode(show);
}

export function $isArticleTitleNode(node: LexicalNode | null | undefined): node is ArticleTitleNode {
    return node instanceof ArticleTitleNode;
}
